import { Link } from "react-router-dom";
import { blogPosts, categories } from "../data/blogPosts";
import { Calendar, Clock, ChevronRight, ArrowRight } from "lucide-react";

const BlogList = () => {
  return (
    <div className="min-h-screen bg-[#F7F7F5] pt-24 pb-16">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="inline-block uppercase text-xs font-manrope font-semibold tracking-[0.2em] text-[#C45A41] mb-4">
            Insurance Resources
          </span>
          <h1 className="font-outfit font-[850] text-4xl sm:text-5xl text-[#143628] tracking-tight mb-6" data-testid="blog-heading">
            Insurance Tips & Guides
          </h1>
          <p className="font-manrope text-[#4A6359] text-lg max-w-2xl mx-auto">
            Expert advice on life insurance, homeowners insurance, and more from Kenneth Carson - your local insurance agent in Elk Grove and Sacramento.
          </p>
        </div>

        {/* Categories */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          <span className="bg-[#143628] text-white px-4 py-2 rounded-full font-manrope text-sm">
            All Posts
          </span>
          {categories.map((category) => (
            <span 
              key={category.slug}
              className="bg-white text-[#4A6359] px-4 py-2 rounded-full font-manrope text-sm border border-[#143628]/10 hover:bg-[#143628] hover:text-white transition-colors cursor-pointer"
            >
              {category.name} ({category.count})
            </span>
          ))}
        </div>

        {/* Blog Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {blogPosts.map((post) => (
            <article 
              key={post.id}
              className="bg-white rounded-3xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-[#143628]/5"
              data-testid={`blog-card-${post.slug}`}
            >
              <Link to={`/blog/${post.slug}`}>
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={post.image} 
                    alt={post.title}
                    className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                  />
                  <span className="absolute top-4 left-4 bg-[#C45A41] text-white px-3 py-1 rounded-full text-xs font-manrope">
                    {post.category}
                  </span>
                </div>
              </Link>
              <div className="p-6">
                <div className="flex items-center gap-4 text-[#4A6359] text-sm font-manrope mb-3">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {new Date(post.publishedDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {post.readTime}
                  </span>
                </div>
                <Link to={`/blog/${post.slug}`}>
                  <h2 className="font-outfit font-bold text-xl text-[#143628] mb-3 hover:text-[#C45A41] transition-colors line-clamp-2">
                    {post.title}
                  </h2>
                </Link>
                <p className="font-manrope text-[#4A6359] text-sm leading-relaxed mb-4 line-clamp-3">
                  {post.excerpt}
                </p>
                <Link 
                  to={`/blog/${post.slug}`}
                  className="inline-flex items-center gap-1 text-[#C45A41] font-manrope font-medium text-sm hover:gap-2 transition-all"
                  data-testid={`read-more-${post.slug}`}
                >
                  Read More <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </article>
          ))}
        </div>

        {/* SEO Content Block */}
        <section className="mt-20 bg-[#EBEAE5] rounded-3xl p-8 md:p-12">
          <h2 className="font-outfit font-bold text-2xl text-[#143628] mb-6">
            Your Local Insurance Resource in Elk Grove & Sacramento
          </h2>
          <div className="grid md:grid-cols-2 gap-8 font-manrope text-[#4A6359] leading-relaxed">
            <div>
              <p className="mb-4">
                Looking for a <strong className="text-[#143628]">life insurance agent near me in Elk Grove, CA</strong>? Kenneth Carson provides expert guidance on term life, whole life, and universal life insurance policies tailored to your family's needs.
              </p>
              <p>
                As a <strong className="text-[#143628]">life insurance agent near me in Natomas Sacramento</strong>, Kenneth offers virtual consultations and personalized service to help Sacramento-area families protect what matters most.
              </p>
            </div>
            <div>
              <p className="mb-4">
                Need a <strong className="text-[#143628]">homeowners insurance agent near me in Natomas Sacramento</strong>? Kenneth helps homeowners find comprehensive coverage at competitive rates, with expertise in the local Sacramento market.
              </p>
              <p>
                Visit our blog for insurance tips, coverage guides, and answers to frequently asked questions about life insurance, home insurance, and more.
              </p>
            </div>
          </div>
          <div className="mt-8 text-center">
            <Link 
              to="/#contact"
              className="inline-flex items-center gap-2 bg-[#C45A41] text-white px-8 py-4 rounded-full font-manrope font-semibold hover:bg-[#A84D37] transition-all duration-300"
              data-testid="blog-list-cta"
            >
              Get Your Free Quote
              <ChevronRight className="w-5 h-5" />
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
};

export default BlogList;
